import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface Plan {
  id: string;
  code: string;
  name: string;
  price_cents: number;
  description: string | null;
}

interface AssinaturaEditPlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  currentPlanCode: string;
  onSuccess: () => void;
}

export function AssinaturaEditPlanModal({
  open,
  onOpenChange,
  subscriptionId,
  currentPlanCode,
  onSuccess
}: AssinaturaEditPlanModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadPlans();
    }
  }, [open]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("display_order");

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Erro ao carregar planos");
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlan) {
      toast.error("Selecione um plano");
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('pagarme-upgrade-subscription', {
        body: {
          new_plan_code: selectedPlan,
          target_subscription_id: subscriptionId,
        }
      });

      if (error) throw error;

      toast.success("Plano atualizado com sucesso");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao atualizar plano");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Modificar Plano</DialogTitle>
          <DialogDescription>
            Selecione o novo plano para esta assinatura
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loadingPlans ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => {
                const isCurrent = plan.code === currentPlanCode;
                const isSelected = plan.code === selectedPlan;

                return (
                  <Card
                    key={plan.id}
                    className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary' : 'hover:border-primary/50'
                      } ${isCurrent ? 'opacity-60' : ''}`}
                    onClick={() => !isCurrent && setSelectedPlan(plan.code)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{plan.name}</h3>
                            {isCurrent && (
                              <Badge variant="secondary">Plano Atual</Badge>
                            )}
                          </div>
                          <p className="text-2xl font-bold text-primary mb-2">
                            {formatPrice(plan.price_cents)}
                            <span className="text-sm font-normal text-muted-foreground">/mês</span>
                          </p>
                          {plan.description && (
                            <p className="text-sm text-muted-foreground">{plan.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0 ml-4">
                            <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                              <Check className="h-4 w-4 text-primary-foreground" />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdatePlan}
              disabled={loading || !selectedPlan || selectedPlan === currentPlanCode}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Atualizando...
                </>
              ) : (
                "Confirmar Alteração"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
